import { MappedDriverItem } from 'reportViewer'

/**
 * Get gene name contained in specified driver.
 *
 * @param {MappedDriverItem} driver - Driver
 * @returns Gene name of driver
 */
export const getGeneName = (driver: Partial<MappedDriverItem>): string => {
    return driver?.gene ? driver.gene.name ?? '' : ''
}

/**
 * Gets final mutation name of driver by combining the gene and mutation names.
 *
 * @param {MappedDriverItem} driver - Driver
 * @returns Mutation name of driver combined by gene and mutation names
 */
export const getMutationName = (
    driver: Partial<MappedDriverItem>,
    t?: (key: string) => string
): string => {
    const { mutation } = driver || {}

    let mutationName: string = getGeneName(driver)

    if (mutation && mutation.name) {
        mutationName = `${mutationName}-${mutation.name}`
    }

    if (!mutation && t) {
        mutationName = getFormattedBiomarkerName(driver, t)
    }

    return mutationName
}

/**
 * Gets test type and test result of the driver in a format which is consumable
 * by translate functions.
 *
 * @param {MappedDriverItem} driver - Driver
 * @returns Test type and test result as a translation key
 */
export const getTestTypeAndTestResultKey = (
    driver: Partial<MappedDriverItem>
): string => {
    let testTypeAndTestResultKey: string = 'UNKNOWN'

    if (driver && 'testType' in driver && 'testResult' in driver) {
        const { testType, testResult } = driver

        if (testType) {
            testTypeAndTestResultKey = testType

            if (testResult) {
                testTypeAndTestResultKey = `${testType}_${testResult}`
            }
        }
    }

    return testTypeAndTestResultKey
}

/**
 * Returns formatted driver name including the test type and test result.
 *
 * @param {*} driver Formattable driver
 * @param {*} t Translation function
 * @param {*} testObject Formattable driver
 * @returns Formatted driver name
 */
export const getFormattedBiomarkerName: (
    driver: Partial<MappedDriverItem>,
    t: (key: string) => string,
    testObject?: MappedDriverItem
) => string = (driver, t, testObject) => {
    const biomarkerNameParts = [
        getMutationName(driver),
        t(
            `common:testTypeAndTestResult.${getTestTypeAndTestResultKey(
                testObject ? testObject : driver
            )}`
        )
    ].filter((part) => part)

    return biomarkerNameParts.join(' ')
}
